import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Accordion,
  Spinner,
  Card,
  Button,
  Modal,
} from "react-bootstrap";
import {
  FaPlay,
  FaDownload,
  FaYoutube,
  FaFilePdf,
  FaEye,
} from "react-icons/fa";

const LiveClass = () => {
  const [courseContent, setCourseContent] = useState([]);
  const [imagePath, setImagePath] = useState("");
  const [currentItem, setCurrentItem] = useState(null);
  const [currentType, setCurrentType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState("");
  const sub_Id = localStorage.getItem("con_Id");

  const fetchPackageDetail = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}/course-chaptar-details-video-pdf`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ chapter_id: sub_Id }),
        }
      );
      const json = await res.json();

      if (json.status === "1" && json.data && json.data.length) {
        const transformedData = json.data.map((item) => ({
          id: item.id,
          chapter_id: item.chapter_id,
          pdf: item.pdf || null,
          pdf_title: item.pdf_title || null,
          video: item.video || null,
          video_title: item.video_title || null,
          video_thumbnail: item.video_thumbnail || null,
        }));

        setCourseContent(transformedData);
        setImagePath(json.imagePath || "");

        if (transformedData.length > 0) {
          const firstItem = transformedData[0];
          if (firstItem.video) {
            handleItemSelect(firstItem, "video");
          } else if (firstItem.pdf) {
            handleItemSelect(firstItem, "pdf");
          }
        }
      } else {
        setError("No content available for this chapter");
      }
    } catch (err) {
      console.error("Error fetching content:", err);
      setError("Failed to load content. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sub_Id) {
      fetchPackageDetail();
    } else {
      setLoading(false);
      setError("No chapter selected");
    }
  }, [sub_Id]);

  const cleanYouTubeUrl = (url) => {
    if (!url) return null;
    const cleanedUrl = url.replace(imagePath, "");

    let videoId = "";
    if (cleanedUrl.includes("youtube.com/shorts/")) {
      videoId = cleanedUrl.split("/shorts/")[1];
    } else if (cleanedUrl.includes("youtu.be/")) {
      videoId = cleanedUrl.split("youtu.be/")[1];
    } else if (cleanedUrl.includes("youtube.com/watch?v=")) {
      videoId = cleanedUrl.split("v=")[1];
      const ampersandPosition = videoId.indexOf("&");
      if (ampersandPosition !== -1) {
        videoId = videoId.substring(0, ampersandPosition);
      }
    }

    return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&controls=1&disablekb=1&fs=0`;
  };

  const handleItemSelect = (item, type) => {
    if (type === "video" && item.video) {
      const embedUrl = cleanYouTubeUrl(item.video);

      if (embedUrl) {
        setCurrentItem({
          type: "video",
          url: embedUrl,
          title: item.video_title,
        });
        setCurrentType("video");
        setError("");
      } else {
        setError("Invalid video URL");
      }
    }

    if (type === "pdf" && item.pdf) {
      setCurrentItem({
        type: "pdf",
        url: item.pdf,
        title: item.pdf_title,
      });
      setCurrentType("pdf");
      setError("");
    }
  };

  const handleViewPdf = (pdfUrl) => {
    setSelectedPdf(pdfUrl);
    setShowPdfModal(true);
  };

  const videos = courseContent.filter((item) => item.video);
  const pdfs = courseContent.filter((item) => item.pdf);

  return (
    <section className="LiveClass inner mb-5">
      <div className="page_header"></div>
      <div className="live-class-container mt-2">
        <div className="lesson__content">
          <Container>
            <h3>Course Content</h3>
            <p>Access all your learning materials in one place</p>
          </Container>
        </div>

        <Container className="mt-4">
          {loading ? (
            <div className="text-center p-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Loading Content...</p>
            </div>
          ) : error ? (
            <div className="text-center p-5">
              <div className="alert alert-warning" role="alert">
                {error}
              </div>
            </div>
          ) : (
            <Row>
              {/* Sidebar */}
              <Col lg={4}>
                <Card className="sidebar-card">
                  <Card.Header>
                    <h5 className="mb-0">Course Content</h5>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <Accordion defaultActiveKey={["videos", "pdfs"]} alwaysOpen>
                      {/* Videos Section */}
                      {videos.length > 0 && (
                        <Accordion.Item eventKey="videos">
                          <Accordion.Header>
                            <div className="d-flex align-items-center w-100">
                              <FaPlay className="me-2 color-primary" />
                              <span>Video Lessons</span>
                              <span className="badge bg-primary ms-auto me-1">
                                {videos.length}
                              </span>
                            </div>
                          </Accordion.Header>
                          <Accordion.Body className="p-0">
                            <div className="list-group list-group-flush">
                              {videos.map((item) => (
                                <div
                                  key={`video-${item.id}`}
                                  className={`list-group-item bg-primary-2 list-group-item-action ${
                                    currentItem?.title === item.video_title
                                      ? "active"
                                      : ""
                                  }`}
                                  onClick={() =>
                                    handleItemSelect(item, "video")
                                  }
                                  style={{ cursor: "pointer" }}
                                >
                                  <div className="d-flex align-items-center">
                                    <FaYoutube className="text-white me-2" />
                                    <div className="flex-grow-1">
                                      <div className="fw-medium">
                                        {item.video_title || "Untitled Video"}
                                      </div>
                                      <small className="text-white">
                                        Video lesson
                                      </small>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </Accordion.Body>
                        </Accordion.Item>
                      )}

                      {/* PDFs Section */}
                      {pdfs.length > 0 && (
                        <Accordion.Item eventKey="pdfs">
                          <Accordion.Header>
                            <div className="d-flex align-items-center w-100">
                              <FaDownload className="me-2 color-primary" />
                              <span>PDF Materials</span>
                              <span className="badge bg-success ms-auto me-1">
                                {pdfs.length}
                              </span>
                            </div>
                          </Accordion.Header>
                          <Accordion.Body className="p-0">
                            <div className="list-group list-group-flush">
                              {pdfs.map((item) => (
                                <div
                                  key={`pdf-${item.id}`}
                                  className={`list-group-item bg-primary-2 list-group-item-action ${
                                    currentItem?.title === item.pdf_title
                                      ? "active"
                                      : ""
                                  }`}
                                  onClick={() => handleItemSelect(item, "pdf")}
                                  style={{ cursor: "pointer" }}
                                >
                                  <div className="d-flex align-items-center">
                                    <FaFilePdf className="text-white me-2" />
                                    <div className="flex-grow-1">
                                      <div className="fw-medium">
                                        {item.pdf_title || "Untitled PDF"}
                                      </div>
                                      <small className="text-white">
                                        PDF document
                                      </small>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </Accordion.Body>
                        </Accordion.Item>
                      )}
                    </Accordion>

                    {videos.length === 0 && pdfs.length === 0 && (
                      <div className="p-3 text-center">
                        <p className="text-muted">No content available</p>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              {/* Main Content */}
              <Col lg={8}>
                <Card className="main-content-card">
                  <Card.Header>
                    <h5 className="mb-0">
                      {currentItem
                        ? currentItem.title
                        : "Select a lesson to begin"}
                    </h5>
                  </Card.Header>
                  <Card.Body className="text-center">
                    {!currentItem && (
                      <div className="empty-state">
                        <div className="empty-icon mb-3">
                          <FaPlay size="3rem" className="text-muted" />
                        </div>
                        <h5>Select a lesson to begin</h5>
                        <p className="text-muted">
                          Choose from the video lessons or PDF materials on the
                          left
                        </p>
                      </div>
                    )}

                    {error && (
                      <div className="alert alert-danger" role="alert">
                        {error}
                      </div>
                    )}

                    {/* Video Player */}
                    {currentItem && currentType === "video" && (
                      <div className="video-container">
                        <div className="ratio ratio-16x9">
                          <iframe
                            src={currentItem.url}
                            title={currentItem.title}
                            allowFullScreen
                            style={{ border: "none", borderRadius: "8px" }}
                          ></iframe>
                        </div>
                        <div className="mt-3">
                          <h5>{currentItem.title}</h5>
                          <p className="text-muted">Now playing</p>
                        </div>
                      </div>
                    )}

                    {/* PDF Preview */}
                    {currentItem && currentType === "pdf" && (
                      <div>
                        <div className="pdf-preview mb-3 p-4 border rounded bg-light">
                          <div className="d-flex justify-content-center align-items-center mb-3">
                            <FaFilePdf
                              size="3rem"
                              className="text-danger me-3"
                            />
                            <h4>{currentItem.title}</h4>
                          </div>
                          <p className="text-muted mb-4">
                            Click below to view or download this PDF document
                          </p>
                          <div className="d-flex justify-content-center gap-3">
                            <button className="educate-btn"
                              size="lg"
                              onClick={() => handleViewPdf(currentItem.url)}
                            >
                              <FaEye className="me-2 fs-5" /> View PDF
                            </button>
                            {/* <Button
                              variant="success"
                              size="lg"
                              href={currentItem.url}
                              target="_blank"
                              download
                            >
                              <FaDownload className="me-2" /> Download
                            </Button> */}
                          </div>
                        </div>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Container>
      </div>

      {/* PDF Modal with Custom PDF Viewer */}
      <Modal style={{zIndex:"9999"}}
        size="xl"
        show={showPdfModal}
        onHide={() => setShowPdfModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>PDF Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ height: "80vh", overflow: "hidden", padding: 0 }}>
          {selectedPdf && <PdfViewer pdfUrl={selectedPdf} />}
        </Modal.Body>
        <Modal.Footer>
          <button className="educate-btn" onClick={() => setShowPdfModal(false)}>
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </section>
  );
};

// PDF Viewer Component
const PdfViewer = ({ pdfUrl }) => {
  const [pdfDocument, setPdfDocument] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  const [scale, setScale] = useState(1.0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const canvasRef = useRef(null);

  const [diblevalue, setdiblevalue] = useState("");

  const handleContextMenu = (e) => {
    e.preventDefault(); // default right-click menu ko roke
    setdiblevalue("Right-click detected on canvas!");
  };

  useEffect(() => {
    // Check if PDF.js is already loaded
    if (window.pdfjsLib) {
      loadPdf();
      return;
    }

    // Load PDF.js library dynamically
    const script = document.createElement("script");
    script.src =
      "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.4.120/pdf.min.js";
    script.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.4.120/pdf.worker.min.js";
      loadPdf();
    };
    script.onerror = () => {
      setError("Failed to load PDF viewer. Please try again later.");
      setLoading(false);
    };
    document.head.appendChild(script);

    return () => {
      if (script.parentNode) {
        document.head.removeChild(script);
      }
    };
  }, [pdfUrl]);

  useEffect(() => {
    if (pdfDocument) {
      renderPage();
    }
  }, [pdfDocument, currentPage, scale]);

  const loadPdf = () => {
    setLoading(true);
    setError(null);

    // Load the PDF
    const loadingTask = window.pdfjsLib.getDocument(pdfUrl);
    loadingTask.promise
      .then(function (pdf) {
        setPdfDocument(pdf);
        setPageCount(pdf.numPages);
        setCurrentPage(1);
        setLoading(false);
      })
      .catch(function (error) {
        console.error("Error loading PDF:", error);
        setError("Failed to load PDF document. Please try again later.");
        setLoading(false);
      });
  };

  const renderPage = () => {
    if (!pdfDocument || !canvasRef.current) return;

    pdfDocument.getPage(currentPage).then(function (page) {
      const viewport = page.getViewport({ scale: scale });
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");

      canvas.height = viewport.height;
      canvas.width = viewport.width;

      const renderContext = {
        canvasContext: context,
        viewport: viewport,
      };

      page.render(renderContext);
    });
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < pageCount) {
      setCurrentPage(currentPage + 1);
    }
  };

  const zoomIn = () => {
    setScale((prevScale) => prevScale + 0.25);
  };

  const zoomOut = () => {
    if (scale > 0.5) {
      setScale((prevScale) => prevScale - 0.25);
    }
  };

  return (
    <div
      className="pdf-viewer-container"
      style={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      <div
        className="viewer-container"
        style={{ flex: 1, overflow: "auto", position: "relative" }}
      >
        {loading && (
          <div
            className="loading"
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          >
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Loading PDF document...</p>
          </div>
        )}
        {error && <div className="error alert alert-danger m-3">{error}</div>}
        {!loading && !error && (
          <>
            <div
              style={{
                position: "absolute",
                top: "10px",
                left: "10px",
                color: "rgba(0,0,0,0.3)",
                fontSize: "20px",
                fontWeight: "bold",
                pointerEvents: "none",
              }}
            >
              Preview Only
            </div>
            <div
              id="pdf-container"
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "20px",
              }}
            >
              <div className="canvas-container">
                <canvas
                  onContextMenu={handleContextMenu} // right-click event
                  ref={canvasRef}
                ></canvas>
              </div>
            </div>
          </>
        )}
      </div>

      {!loading && !error && (
        <div
          className="controls"
          style={{
            padding: "10px",
            backgroundColor: "#f8f9fa",
            borderTop: "1px solid #dee2e6",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "15px",
          }}
        >
          <Button
            variant="outline-primary"
            onClick={goToPrevPage}
            disabled={currentPage <= 1}
          >
            Previous
          </Button>
          <span className="page-info">
            Page <span style={{ fontWeight: "bold" }}>{currentPage}</span> of{" "}
            <span style={{ fontWeight: "bold" }}>{pageCount || "--"}</span>
          </span>
          <Button
            variant="outline-primary"
            onClick={goToNextPage}
            disabled={currentPage >= pageCount}
          >
            Next
          </Button>
          <div
            className="zoom-controls"
            style={{ display: "flex", gap: "5px" }}
          >
            <Button variant="outline-secondary" onClick={zoomOut}>
              -
            </Button>
            <Button variant="outline-secondary" onClick={zoomIn}>
              +
            </Button>
          </div>
        </div>
      )}

      {diblevalue && (
        <div
          style={{
            marginTop: "10px",
            padding: "8px 15px",
            backgroundColor: "#ffe6e6", // light red background
            color: "#b30000", // dark red text
            fontWeight: "bold",
            fontSize: "16px",
            borderRadius: "8px",
            border: "1px solid #ff4d4d",
            boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
            textAlign: "center",
            minWidth: "250px",
            animation: "fadeIn 0.3s ease-in-out", // simple fade-in effect
          }}
        >
          {diblevalue}
        </div>
      )}
    </div>
  );
};

export default LiveClass;
